/** Plain-language food badges: where the numbers come from, and whether a label adds up. */
import { useState, type ReactElement } from 'react';
import type { TrueLabelReport } from '@engine/tef';
import { VERDICT_PLAIN } from '../lib/labelCheck';
import { foodTrust } from '../lib/trust';
import type { FoodSource } from '../lib/types';
import { Icon } from './Icon';
import { TrueLabelReportView } from './TrueLabelChecker';

/** "Lab-tested (USDA) · Full nutrient detail", with the exact count on hover. */
export function TrustBadge({ source, count, aiCount = 0, compact = false }: { source: FoodSource; count: number; aiCount?: number; compact?: boolean }): ReactElement {
  const t = foodTrust(source, count, aiCount);
  return (
    <span className="trust" title={t.title}>
      <span className={`trust-pill t-${t.tone}`}>{t.label}</span>
      {!compact && <span className="trust-detail">{t.detail}</span>}
    </span>
  );
}

/** One line saying whether the printed calories match the printed macros; the full report on demand. */
export function LabelCheckLine({ report, servingLabel }: { report: TrueLabelReport; servingLabel?: string }): ReactElement {
  const [open, setOpen] = useState<boolean>(false);
  const plain = VERDICT_PLAIN[report.verdict];
  const gap: string = report.unexplainedGapKcal > 0 ? ` About ${Math.round(report.unexplainedGapKcal)} kcal per serving can’t be explained by rounding.` : '';
  return (
    <div className={`label-check lc-${plain.tone}`}>
      <div className="lc-head">
        <Icon name={plain.tone === 'ok' ? 'check' : 'info'} size={16} />
        <span><b>True Label check{servingLabel ? ` (${servingLabel})` : ''}:</b> {plain.text}{gap}</span>
        <button type="button" className="link-btn lc-more" onClick={() => setOpen((o) => !o)}>{open ? 'hide' : 'details'}</button>
      </div>
      {open && <div style={{ marginTop: 10 }}><TrueLabelReportView report={report} /></div>}
    </div>
  );
}
